import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogFooter, InputGroup, NumericInput, Spinner } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  allocatePurchase, fetchBatches, fetchLeadSources, fetchMappingProfiles, fetchOverlapReport, fetchPaymentCandidates, fetchPurchases,
  fetchSourceReport, fetchVerificationAnswerCounts, linkPayment, purchaseFromPayment, removeAllocation, savePurchase, unlinkPayment,
  type PurchaseRow, type QuickBooksPayment,
} from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { Card, Empty, ErrorNote, Pill, Segmented, VerifyCell, fmtDay, fmtInt, fmtMoney, fmtWhen } from './ui';
import { FileChip } from './FileChip';
import { PickList } from '../ui/PickList';
import { MaterializedReportStatus } from '../ui/MaterializedReportStatus';

/* ── Purchases ──────────────────────────────────── */

const EMPTY_PURCHASE = { lead_source: '', amount: 0, purchased_on: '', invoice_reference: '', note: '' };

/** [spec v4 F] Paid: the QuickBooks payments linked to the purchase against its amount. */
function paidPill(p: PurchaseRow) {
  const paid = Number(p.paid);
  const amount = Number(p.amount);
  if (paid === 0) return <Pill tone="muted">No payment linked</Pill>;
  if (Math.abs(paid - amount) < 0.005) return <Pill tone="success">Paid</Pill>;
  if (paid < amount) return <Pill tone="warn">{fmtMoney(amount - paid)} unpaid</Pill>;
  return <Pill tone="info">{fmtMoney(paid - amount)} overpaid</Pill>;
}

function allocatedPill(p: PurchaseRow) {
  const open = Number(p.amount) - Number(p.allocated);
  if (open <= 0.005) return <Pill tone="success">Allocated</Pill>;
  return <Pill tone="warn">{fmtMoney(open)} to allocate</Pill>;
}

export function PurchasesView() {
  const queryClient = useQueryClient();
  const purchases = useQuery({ queryKey: leadImportKeys.purchases, queryFn: fetchPurchases, ...queryPolicies.pagedData });
  const sources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, ...queryPolicies.configuration });
  const [form, setForm] = useState(EMPTY_PURCHASE);
  const [formOpen, setFormOpen] = useState(false);
  const [picker, setPicker] = useState<{ purchase: PurchaseRow | null } | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [show, setShow] = useState<'all' | 'unpaid' | 'unallocated'>('all');
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const refresh = () => queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
  const expand = (id: number) => setExpanded((s) => new Set(s).add(id));

  const create = useMutation({
    mutationFn: () => savePurchase({ ...form, lead_source: Number(form.lead_source) }),
    onSuccess: (id) => { toast.success('Purchase saved.'); setFormOpen(false); setForm(EMPTY_PURCHASE); expand(id); refresh(); },
    onError: (e) => toast.error((e as Error).message),
  });

  const all = purchases.data ?? [];
  const rows = all.filter((p) => (!sourceFilter || p.lead_source === Number(sourceFilter))
    && (show === 'all' || (show === 'unpaid' ? Number(p.paid) < Number(p.amount) - 0.005 : Number(p.allocated) < Number(p.amount) - 0.005)));
  const total = (key: 'amount' | 'paid' | 'allocated') => rows.reduce((n, p) => n + Number(p[key]), 0);
  const toggle = (id: number) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="li-stack">
      <Dialog isOpen={formOpen} onClose={() => setFormOpen(false)} title="New purchase" style={{ width: 'min(460px, 100vw)' }}>
        <DialogBody>
          <div className="li-form li-form--tight">
            <label className="li-field"><span className="li-field__label">Source</span>
              <PickList value={form.lead_source} onChange={(v) => setForm({ ...form, lead_source: v })} fill ariaLabel="Source" placeholder="Choose a source"
                options={(sources.data ?? []).map((s) => ({ value: String(s.id), label: s.source }))} />
            </label>
            <div className="li-row">
              <label className="li-field"><span className="li-field__label">Amount</span>
                <NumericInput leftIcon="dollar" min={0} value={form.amount || ''} onValueChange={(v) => setForm({ ...form, amount: v || 0 })} buttonPosition="none" fill />
              </label>
              <label className="li-field"><span className="li-field__label">Purchased on</span>
                <input type="date" className="bp5-input" value={form.purchased_on} onChange={(e) => setForm({ ...form, purchased_on: e.target.value })} />
              </label>
            </div>
            <label className="li-field"><span className="li-field__label">Invoice or payment reference <span className="li-muted">optional</span></span>
              <InputGroup value={form.invoice_reference} onChange={(e) => setForm({ ...form, invoice_reference: e.target.value })} />
            </label>
            <label className="li-field"><span className="li-field__label">Note <span className="li-muted">optional</span></span>
              <InputGroup value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
            <p className="li-field__help">After saving, link the QuickBooks payment and split the amount across the imports it paid for.</p>
          </div>
        </DialogBody>
        <DialogFooter actions={<>
          <Button minimal onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button intent="primary" disabled={!form.lead_source || !(form.amount > 0)} loading={create.isPending} onClick={() => create.mutate()}>Save purchase</Button>
        </>} />
      </Dialog>
      {picker && (
        <PaymentPicker purchase={picker.purchase} sources={sources.data ?? []} onClose={() => setPicker(null)}
          onCreated={(id) => { expand(id); refresh(); }} onLinked={refresh} />
      )}

      <div className="li-purch-bar">
        <label>Source
          <PickList value={sourceFilter} onChange={setSourceFilter} ariaLabel="Source"
            options={[{ value: '', label: 'All sources' }, ...(sources.data ?? []).filter((s) => all.some((p) => p.lead_source === s.id)).map((s) => ({ value: String(s.id), label: s.source }))]} />
        </label>
        <Segmented size="sm" ariaLabel="Show" value={show} onChange={setShow}
          options={[{ value: 'all', label: 'All' }, { value: 'unpaid', label: 'Not fully paid' }, { value: 'unallocated', label: 'Not fully allocated' }]} />
        <span className="li-purch-bar__summary" data-testid="purchase-summary">
          <b>{rows.length}</b> purchase{rows.length === 1 ? '' : 's'}, <b>{fmtMoney(total('amount'))}</b> spent,{' '}
          <b>{fmtMoney(total('paid'))}</b> paid in QuickBooks, <b>{fmtMoney(total('allocated'))}</b> allocated to imports
        </span>
      </div>

      <Card title="Purchases" subtitle="What each lead file cost, the QuickBooks payments that paid it, and the imports it covers." flush
        actions={<>
          <button type="button" className="li-btn li-btn--secondary" onClick={() => setPicker({ purchase: null })}>From a QuickBooks payment</button>
          <button type="button" className="li-btn li-btn--primary" onClick={() => setFormOpen(true)}>New purchase</button>
        </>}>
        <ErrorNote error={purchases.error} />
        {purchases.isLoading ? <div className="li-empty"><Spinner size={20} /></div>
          : all.length === 0 ? <Empty title="No purchases yet">Record a purchase, or start from its QuickBooks payment, to see cost per lead in Reports.</Empty>
          : rows.length === 0 ? <Empty title="No purchases match">Choose another source or show all purchases.</Empty> : (
          <div className="li-tablewrap">
            <table className="li-table li-table--middle li-purch">
              <thead><tr><th aria-label="Details" /><th>Purchased</th><th>Source</th><th>Reference</th><th className="r">Amount</th><th>Paid</th><th>Allocated</th><th>Imports</th></tr></thead>
              {rows.map((p) => {
                const isOpen = expanded.has(p.id);
                return (
                  <tbody key={p.id} className={`li-purch__group${isOpen ? ' is-open' : ''}`} data-testid={`purchase-${p.id}`}>
                    <tr className="is-clickable" tabIndex={0} aria-expanded={isOpen} onClick={() => toggle(p.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(p.id); } }}>
                      <td className="li-purch__chev">{isOpen ? '▾' : '▸'}</td>
                      <td>{fmtDay(p.purchased_on)}</td>
                      <td className="strong">{p.source}</td>
                      <td className="li-muted">{p.invoice_reference ?? '-'}</td>
                      <td className="r num strong">{fmtMoney(p.amount)}</td>
                      <td>{paidPill(p)}</td>
                      <td>{allocatedPill(p)}</td>
                      <td className="li-muted">{p.allocations.length === 0 ? 'None yet' : `${p.allocations.length} import${p.allocations.length === 1 ? '' : 's'}`}</td>
                    </tr>
                    {isOpen && (
                      <tr className="li-purch__detail"><td colSpan={8}>
                        <PurchaseDetail purchase={p} onLink={() => setPicker({ purchase: p })} onChanged={refresh} />
                      </td></tr>
                    )}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** The expanded purchase: what paid it on the left, what it paid for on the right. */
function PurchaseDetail({ purchase: p, onLink, onChanged }: { purchase: PurchaseRow; onLink: () => void; onChanged: () => void }) {
  const batches = useQuery({ queryKey: leadImportKeys.batches({ status: 'Committed' }), queryFn: () => fetchBatches({ status: 'Committed' }), ...queryPolicies.pagedData });
  const open = Number(p.amount) - Number(p.allocated);
  const [alloc, setAlloc] = useState<{ batch: string; amount: number | null }>({ batch: '', amount: null });
  const amount = alloc.amount ?? open;
  const fail = (e: unknown) => toast.error((e as Error).message);
  const allocate = useMutation({
    mutationFn: () => allocatePurchase(p.id, Number(alloc.batch), amount),
    onSuccess: () => { toast.success('Allocated.'); setAlloc({ batch: '', amount: null }); onChanged(); }, onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: number) => removeAllocation(id), onSuccess: onChanged, onError: fail });
  const unlink = useMutation({ mutationFn: (id: number) => unlinkPayment(id), onSuccess: () => { toast.success('Payment unlinked.'); onChanged(); }, onError: fail });
  const choices = (batches.data ?? []).filter((b) => b.lead_source === p.lead_source && !p.allocations.some((a) => a.batch === b.id));

  return (
    <div className="li-purch__panels">
      <section className="li-purch__panel" aria-label="Paid by">
        <header><b>Paid by</b><span>QuickBooks payments</span>
          <button type="button" className="li-btn li-btn--secondary li-purch__headbtn" onClick={onLink}>Link a payment</button></header>
        {p.payments.length === 0 ? <p className="li-purch__none">No QuickBooks payment linked yet.</p> : (
          <table className="li-table li-table--compact li-table--middle">
            <tbody>
              {p.payments.map((x) => (
                <tr key={x.id}>
                  <td>{fmtDay(x.txn_date)}</td>
                  <td><div className="strong">{x.payee ?? '-'}</div><div className="li-muted li-purch__memo">{[x.doc_number, x.memo].filter(Boolean).join(', ')}</div></td>
                  <td className="r num">{fmtMoney(x.paid)}</td>
                  <td className="r"><Button small minimal icon="cross" aria-label={`Unlink payment of ${fmtMoney(x.paid)}`} loading={unlink.isPending && unlink.variables === x.id} onClick={() => unlink.mutate(x.id)} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={2}>Paid of {fmtMoney(p.amount)}</td><td className="r num">{fmtMoney(p.paid)}</td><td /></tr></tfoot>
          </table>
        )}
      </section>
      <section className="li-purch__panel" aria-label="Covers these imports">
        <header><b>Covers these imports</b><span>Their cost per lead in Reports</span></header>
        {p.allocations.length > 0 && (
          <table className="li-table li-table--compact li-table--middle">
            <tbody>
              {p.allocations.map((x) => (
                <tr key={x.id}>
                  <td><FileChip batchId={x.batch} name={x.file_name} /></td>
                  <td className="li-muted">{fmtDay(x.batch_date)}</td>
                  <td className="r num">{fmtMoney(x.amount)}</td>
                  <td className="r"><Button small minimal icon="cross" aria-label="Remove allocation" onClick={() => remove.mutate(x.id)} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={2}>Allocated of {fmtMoney(p.amount)}</td><td className="r num">{fmtMoney(p.allocated)}</td><td /></tr></tfoot>
          </table>
        )}
        {open > 0.005 ? (
          <div className="li-purch__alloc">
            <PickList value={alloc.batch} onChange={(v) => setAlloc({ ...alloc, batch: v })} ariaLabel="Import" disabled={!choices.length}
              placeholder={choices.length ? `Add an import from ${p.source}` : `No other ${p.source} imports`}
              options={choices.map((b) => ({ value: String(b.id), label: `${fmtDay(b.batch_date)}, ${b.file_name}` }))} />
            <NumericInput leftIcon="dollar" min={0} value={amount} onValueChange={(v) => setAlloc({ ...alloc, amount: v || 0 })} buttonPosition="none" style={{ width: 110 }} aria-label="Amount" />
            <Button small disabled={!alloc.batch || !(amount > 0)} loading={allocate.isPending} onClick={() => allocate.mutate()}>Allocate</Button>
            <span className="li-muted">{fmtMoney(open)} left</span>
          </div>
        ) : <p className="li-purch__none">The whole amount is allocated.</p>}
      </section>
      {p.note && <p className="li-purch__note">{p.note}</p>}
    </div>
  );
}

/**
 * [spec v4 F] Picks an unlinked QuickBooks payment: links it to `purchase`,
 * or, with no purchase, creates a purchase for the whole payment.
 */
function PaymentPicker({ purchase, sources, onClose, onCreated, onLinked }: {
  purchase: PurchaseRow | null; sources: { id: number; source: string | null }[];
  onClose: () => void; onCreated: (id: number) => void; onLinked: () => void;
}) {
  const [search, setSearch] = useState('');
  const [fallbackSource, setFallbackSource] = useState('');
  const candidates = useQuery({
    queryKey: leadImportKeys.paymentCandidates(purchase?.id ?? null, search),
    queryFn: () => fetchPaymentCandidates(purchase?.id ?? null, search), ...queryPolicies.pagedData,
  });
  const fail = (e: unknown) => toast.error((e as Error).message);
  const link = useMutation({
    mutationFn: (transaction: number) => linkPayment(purchase!.id, transaction),
    onSuccess: () => { toast.success('Payment linked.'); onLinked(); onClose(); }, onError: fail,
  });
  const create = useMutation({
    mutationFn: (t: QuickBooksPayment) => purchaseFromPayment(t.transaction, t.lead_source ?? Number(fallbackSource)),
    onSuccess: (id) => { toast.success('Purchase created. Allocate it to the imports it paid for.'); onCreated(id); onClose(); }, onError: fail,
  });
  const list = candidates.data ?? [];
  const unfiled = !purchase && list.some((c) => c.lead_source == null);

  return (
    <Dialog isOpen onClose={onClose} title={purchase ? `Link a QuickBooks payment to ${purchase.source} ${fmtMoney(purchase.amount)}` : 'New purchase from a QuickBooks payment'}
      style={{ width: 'min(760px, 100vw)' }}>
      <DialogBody>
        <p className="li-field__help li-purch__pickhelp">
          {purchase ? 'Payments from this source and of this amount come first. One payment pays one purchase; for a bulk payment that covered several files, allocate the purchase to each import.'
            : 'Creates a purchase for the whole payment, on its date, with its QuickBooks reference. Then allocate it to the imports it paid for.'}
        </p>
        <div className="li-purch__pickbar">
          <InputGroup leftIcon="search" placeholder="Search payee, memo, reference or amount" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search payments" />
          {unfiled && (
            <PickList value={fallbackSource} onChange={setFallbackSource} ariaLabel="Source for unfiled payments" placeholder="Source for unfiled payments"
              options={sources.map((s) => ({ value: String(s.id), label: s.source ?? `Source ${s.id}` }))} />
          )}
        </div>
        <ErrorNote error={candidates.error} />
        {candidates.isLoading ? <div className="li-empty"><Spinner size={20} /></div> : list.length === 0 ? (
          <Empty title={search ? 'No payments match' : 'No unlinked payments'}>
            {search ? 'Try the payee name or the amount.' : 'Lead payments appear here after QuickBooks is pulled on Commissions, Bank.'}
          </Empty>
        ) : (
          <div className="li-tablewrap li-tablewrap--scroll">
            <table className="li-table li-table--compact li-table--middle" data-testid="payment-candidates">
              <thead><tr><th>Date</th><th>Paid to</th><th>Filed as</th><th className="r">Amount</th><th /></tr></thead>
              <tbody>
                {list.map((c) => {
                  const otherSource = !!purchase && c.lead_source != null && c.lead_source !== purchase.lead_source;
                  const exact = !!purchase && Math.abs(Number(c.paid) - Number(purchase.amount)) < 0.005;
                  const needsSource = !purchase && c.lead_source == null && !fallbackSource;
                  return (
                    <tr key={c.transaction} className={otherSource ? 'is-muted' : undefined} data-testid={`candidate-${c.transaction}`}>
                      <td>{fmtDay(c.txn_date)}</td>
                      <td><div className="strong">{c.payee ?? '-'}</div><div className="li-muted li-purch__memo">{[c.doc_number, c.memo].filter(Boolean).join(', ')}</div></td>
                      <td>{c.source ? <Pill tone={otherSource ? 'muted' : 'info'}>{c.source}</Pill> : <Pill tone="muted">Not filed</Pill>}</td>
                      <td className="r num strong">{fmtMoney(c.paid)}{exact && <div><Pill tone="success">Exact amount</Pill></div>}</td>
                      <td className="r">
                        {purchase ? (
                          <Button small disabled={otherSource} title={otherSource ? `Filed under ${c.source}` : undefined}
                            loading={link.isPending && link.variables === c.transaction} onClick={() => link.mutate(c.transaction)}>Link</Button>
                        ) : (
                          <Button small disabled={needsSource} title={needsSource ? 'Choose the source for unfiled payments first' : undefined}
                            loading={create.isPending && create.variables?.transaction === c.transaction} onClick={() => create.mutate(c)}>Create purchase</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogBody>
      <DialogFooter actions={<Button minimal onClick={onClose}>Close</Button>} />
    </Dialog>
  );
}

/* ── Reports (design 12) ──────────────────────────────── */

export function ReportsView() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = [from || null, to || null] as const;
  const report = useQuery({ queryKey: leadImportKeys.report('sources', ...range), queryFn: () => fetchSourceReport(...range), ...queryPolicies.dashboardAggregate });
  const overlap = useQuery({ queryKey: leadImportKeys.report('overlap', ...range), queryFn: () => fetchOverlapReport(...range), ...queryPolicies.dashboardAggregate });
  const r = report.data;
  const names = useMemo(() => new Map<number, string>((overlap.data?.sources ?? []).map((s: { id: number; source: string }) => [s.id, s.source])), [overlap.data]);

  return (
    <div className="li-stack">
      <div className="li-row li-wrap">
        <span className="li-field__label">Batch date</span>
        <input type="date" className="bp5-input li-date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <span>to</span>
        <input type="date" className="bp5-input li-date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
        <span className="li-muted">Imported files only</span>
      </div>
      <MaterializedReportStatus name="lead_import_reports" refetchKeys={[[...leadImportKeys.all, 'report']]} />
      <ErrorNote error={report.error} />
      {report.isLoading ? <Spinner size={20} /> : r && (
        <>
          <div className="li-tiles">
            <div className="li-tile"><div className="li-tile__label">Leads received</div><div className="li-tile__value">{fmtInt(r.f1_observations)}</div><div className="li-tile__sub">repeats included</div></div>
            <div className="li-tile li-tile--good"><div className="li-tile__label">Unique phones</div><div className="li-tile__value">{fmtInt(r.f3_global_phones)}</div>
              <div className="li-tile__sub">each phone counted once, across every source</div></div>
          </div>
          <Card title="Source scorecard" flush>
            <div className="li-tablewrap">
              <table className="li-table">
                <thead><tr><th>Source</th><th className="r">Leads</th><th className="r">New</th><th className="r" title="Phone was already received from any source">Seen before</th>
                  <th className="r" title="Phone belongs to a merchant (at import time)">Merchant</th><th className="r">Assigned</th><th className="r">Promoted</th>
                  <th className="r">Cost</th><th className="r">Per lead</th><th className="r">Per promoted</th></tr></thead>
                <tbody>
                  {(r.sources ?? []).map((s: Record<string, number | string | null>) => (
                    <tr key={String(s.lead_source)}>
                      <td title={`${fmtInt(s.batches as number)} imports`}><b>{s.source}</b></td><td className="r num">{fmtInt(s.observations as number)}</td>
                      <td className="r num">{fmtInt(s.new_identities as number)}</td><td className="r num">{fmtInt(s.known_earlier as number)}</td>
                      <td className="r num" title={`${fmtInt(s.bought_own_client as number)} were already our client`}>{fmtInt(s.known_merchant as number)}</td>
                      <td className="r num">{fmtInt(s.assigned as number)}</td><td className="r num">{fmtInt(s.promoted as number)}</td>
                      <td className="r num">{s.cost ? fmtMoney(s.cost as number) : '-'}</td>
                      <td className="r num">{fmtMoney(s.cost_per_lead as number | null)}</td>
                      <td className="r num">{fmtMoney(s.cost_per_promoted as number | null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="li-card__foot li-muted">Merchant counts use the CRM as it was when each file was imported. Costs only count imports with an allocated purchase.</p>
          </Card>
          <Card title="Phones shared between sources" flush>
            {(overlap.data?.pairs ?? []).length === 0 ? <Empty>No phones shared between sources in this range.</Empty> : (
              <table className="li-table li-table--compact">
                <thead><tr><th>Source</th><th>Source</th><th className="r">Shared phones</th></tr></thead>
                <tbody>
                  {(overlap.data?.pairs ?? []).sort((a: { phones: number }, b: { phones: number }) => b.phones - a.phones).map((p: { a: number; b: number; phones: number }) => (
                    <tr key={`${p.a}-${p.b}`}><td>{names.get(p.a)}</td><td>{names.get(p.b)}</td><td className="r num">{fmtInt(p.phones)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/* ── Verification answers ─────────────────────────────── */

/**
 * Stored phone and email answers. An answer shows on every lead with that
 * phone or email, and the next verification run reuses it instead of paying
 * again. Past provider files are loaded once by the team, not from here.
 */
export function VerificationView() {
  const counts = useQuery({ queryKey: leadImportKeys.verificationAnswers, queryFn: fetchVerificationAnswerCounts, ...queryPolicies.dashboardAggregate });
  return (
    <div className="li-stack">
      <Card title="Stored answers" subtitle="Each phone and email is checked once. Every lead with that phone or email shows the answer, and later checks reuse it at no cost.">
        <ErrorNote error={counts.error} />
        {counts.isLoading ? <Spinner size={20} /> : (
          <div className="li-tiles" data-testid="li-verification-counts">
            {(['phone', 'email'] as const).map((kind) => {
              const list = (counts.data ?? []).filter((c) => c.kind === kind);
              const answers = list.reduce((n, c) => n + c.answers, 0);
              const imported = list.reduce((n, c) => n + c.imported, 0);
              return (
                <div key={kind} className="li-tile">
                  <div className="li-tile__label">{kind === 'phone' ? 'Phones checked (Veriphone)' : 'Emails checked (MillionVerifier)'}</div>
                  <div className="li-tile__value">{fmtInt(answers)}</div>
                  {imported > 0 && <div className="li-tile__sub">{fmtInt(imported)} from past provider files</div>}
                  <div className="li-row li-wrap li-verify-mix">
                    {list.map((c) => <span key={c.result} className="li-row"><VerifyCell status={c.result} /> <span className="num">{fmtInt(c.answers)}</span></span>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ── Mapping profiles ─────────────────────────────────── */

export function MappingProfilesView() {
  const q = useQuery({ queryKey: leadImportKeys.profiles, queryFn: fetchMappingProfiles, ...queryPolicies.configuration });
  const sources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, ...queryPolicies.configuration });
  const names = new Map((sources.data ?? []).map((s) => [s.id, s.source]));
  return (
    <Card title="Saved mappings" subtitle="Files with the same headers reuse these column choices.">
      <ErrorNote error={q.error} />
      {(q.data ?? []).length === 0 ? <Empty>No saved mappings yet. The first import of each layout creates one.</Empty> : (
        <div className="li-stack">
          {(q.data ?? []).map((p) => {
            const latest = [...p.versions].sort((a, b) => b.version_number - a.version_number)[0];
            return (
              <div key={p.id} className="li-profile">
                <div className="li-row"><b>{p.name}</b>{p.lead_source && <Pill tone="muted">{names.get(p.lead_source)}</Pill>}
                  {latest && <span className="li-muted">updated {fmtWhen(latest.confirmed_at)}{p.versions.length > 1 ? `, ${p.versions.length} versions` : ''}</span>}</div>
                {latest && (
                  <div className="li-mapchips">
                    {latest.headers.map((h) => {
                      const rule = latest.rules.find((r) => r.position === h.position);
                      const phoneRank = latest.phone_priority.indexOf(h.position);
                      const to = phoneRank >= 0 ? `Phone ${phoneRank + 1}`
                        : rule?.use === 'field' ? (rule.target ?? '').replace(/_/g, ' ')
                        : rule?.use === 'name' ? 'full name, split'
                        : rule?.use === 'extra' ? 'extra data' : 'ignored';
                      return (
                        <span key={h.position} className={`li-mapchip${phoneRank >= 0 ? ' is-phone' : rule?.use === 'field' || rule?.use === 'name' ? '' : ' is-keep'}`}>
                          <span className="li-mapchip__from">{h.header || `Column ${h.position + 1}`}</span>
                          <span className="li-mapchip__to">{to}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
